import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';

import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import type { Attribute, AttributeListData } from '@recruitee/http-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';

import type {
  RtButtonColorContextVariant,
  RtButtonVariant,
  RtIconButtonVariant,
  RtInfoboxButtonVariant,
  RtWalkthroughButtonVariant,
} from '../common/button/button.types';
import { ButtonComponent } from '../common/button/components/button/button.component';
import { IconButtonComponent } from '../common/button/components/icon-button/icon-button.component';
import { TagComponent } from '../common/tag/tag.component';
import { WindowRef } from '../common/window-ref';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../core/overlay/overlay-directives';
import { AttributesSelectComponent } from './attributes-select/attributes-select.component';
import type {
  AttributesLoadMore,
  AttributesTranslationsType,
  ListTemplateContext,
  RtUIAttributesTranslationsContexts,
  TranslationsContextType,
} from './attributes.types';
import { RtUIAttributesTranslations } from './attributes.types';

@Component({
  selector: 'rt-attributes',
  templateUrl: './attributes.component.html',
  styleUrls: ['./attributes.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AttributesSelectComponent,
    ButtonComponent,
    ConnectedOverlayDirective,
    IconButtonComponent,
    NgTemplateOutlet,
    OverlayOriginDirective,
    TagComponent,
    TranslatePipe,
  ],
})
export class AttributesComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  // is the list of attributes already applied
  @Input() public attributes: Attribute[] = [];

  // is the list of suggestions based on emitted query
  @Input() public suggestionsData: AttributeListData;

  // flag describing if user can select attribute from existing list of suggestions
  @Input() public canPick: boolean = false;

  // flag describing if user can remove attributes
  @Input() public canRemove: boolean | ((attr: Attribute) => boolean) = false;

  // flag describing if user can create new attributes
  @Input() public canCreate: boolean = false;

  @Input() public createDisabled: boolean = false;
  @Input() public isReadOnly: boolean = false;

  // should it reset input when user hits 'enter'
  @Input() public resetOnSaveFromInput: boolean = true;

  @Input() public hasMarginBottom: boolean = true;
  @Input() public hasMarginTop: boolean = false;
  @Input() public hasSmallXXXMargins: boolean = false;
  @Input() public hasTriggerWidth: boolean = false;
  @Input() public hasTagsFontWeightMedium: boolean = false;

  // should it reset input when user pick attribute from suggestions
  @Input() public resetOnSaveFromSuggestion: boolean = true;

  @Input() public listTpl: TemplateRef<ListTemplateContext> | null;
  @Input() public markSelected = false;

  @Input() public selectFooterTemplate: TemplateRef<any> | null;
  @Input() public selectHeaderTemplate: TemplateRef<any> | null;

  @Input() public dropdownAutoCloseOnUnpreventedClick: boolean = true;

  @Input()
  public set translationsContext(context: TranslationsContextType) {
    if (!this.translationsMapping[context]) {
      throw new Error(
        `Context '${context}' not found in translationsMapping (RtUIAttributesTranslations)`,
      );
    }

    this.attributesTexts = Object.entries(this.translationsMapping[context]).reduce(
      (acc, [k, v]) => ({
        ...acc,
        [k]: v !== undefined ? this.i18n.translate(v) : '',
      }),
      <AttributesTranslationsType>{},
    );
  }

  @Input()
  public set toggle(_: boolean) {
    if (this.dropdown.origin) {
      this.dropdown.open = !this.dropdown.open;
    }
  }

  @Input()
  public set open(value: boolean) {
    if (!this.dropdown.origin) {
      return;
    }

    this.dropdown.open = value;
  }

  @Input()
  public dropdownPositions: ConnectionPositionPair[] | null = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  @Input() public hasSearch: boolean = true;
  @Input() public hasTrigger: boolean = true;
  @Input() public control: UntypedFormControl;

  @Input() public emitQueryOnStart: boolean = true;
  @Input() public initialValue: string = '';
  @Input() public initialOpen: boolean = false;
  @Input() public maxLength: number = 255;
  @Input() public tagsHoverable: boolean = true;
  @Input() public tagsClickable: boolean = true;

  @Input() public itemsLimit?: number;
  @Input() public triggerIconButtonVariant:
    | RtButtonVariant
    | RtIconButtonVariant
    | RtInfoboxButtonVariant
    | RtButtonColorContextVariant = 'outline';
  @Input() public showMoreButtonVariant:
    | RtButtonVariant
    | RtInfoboxButtonVariant
    | RtButtonColorContextVariant
    | RtWalkthroughButtonVariant = 'outline';

  @Output() public query: EventEmitter<string> = new EventEmitter();
  @Output() public save: EventEmitter<Attribute> = new EventEmitter();
  @Output() public delete: EventEmitter<Attribute> = new EventEmitter();
  @Output() public loadMore = new EventEmitter<AttributesLoadMore>();

  @ViewChild('defaultList', { static: true })
  public defaultListTemplate: TemplateRef<ListTemplateContext>;
  @ViewChild('dropdown', { static: true }) public dropdown: ConnectedOverlayDirective;

  public attributeAlreadyExists: boolean = false;
  public maxDropdownHeight: `${number}px` = '250px';
  public attributesTexts: AttributesTranslationsType;
  public removableAttributes: Record<string | number, boolean> = {};
  public showedOverLimit: boolean = false;
  public limitedAttributes: Attribute[] = [];

  private boundToggleDropdown: (origin: OverlayOriginDirective) => void;
  private ngOnDestroy$ = new Subject<void>();

  public get listContext(): ListTemplateContext | null {
    // default template can access public properties from this class so there is no need for creating context for it
    if (this.listTemplate === this.defaultListTemplate) {
      return null;
    }

    return {
      $implicit: {
        attributes: this.attributes,
        canPick: this.canPick,
        canCreate: this.canCreate,
        suggestionsLoading: this.suggestionsData.isLoading,
        toggleDropdown: this.boundToggleDropdown,
        dropdownOpened: this.dropdown.open,
      },
    };
  }

  public get listTemplate(): TemplateRef<ListTemplateContext> {
    return this.listTpl || this.defaultListTemplate;
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private windowRef: WindowRef,
    private i18n: I18nService,
    @Inject(RtUIAttributesTranslations)
    private translationsMapping: RtUIAttributesTranslationsContexts,
  ) {
    this.boundToggleDropdown = this.toggleDropdown.bind(this);
    this.updateDropdownMaxHeight(this.windowRef.nativeWindow.outerHeight);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('suggestionsData' in changes && this.dropdown) {
      setTimeout(() => this.dropdown?.updatePositionWithoutRedrawing());
    }

    if ('attributes' in changes || 'canRemove' in changes) {
      this.removableAttributes = this.getRemovableAttributes();
      this.setLimitedAttributes();
    }
  }

  public ngOnInit(): void {
    this.dropdown.autoCloseOnUnpreventedClick = this.dropdownAutoCloseOnUnpreventedClick;
  }

  public ngAfterViewInit(): void {
    if (this.initialOpen) {
      this.dropdown.open = true;
      this.cdRef.detectChanges();
    }

    observableFromEvent(this.windowRef.nativeWindow, 'resize')
      .pipe(debounceTime(100), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.updateDropdownMaxHeight(this.windowRef.nativeWindow.outerHeight);
        this.dropdown.updatePositionWithoutRedrawing();
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public handleQueryChange(query: string): void {
    this.attributeAlreadyExists = !!this.attributes.find(
      attr => attr.value.toLowerCase() === query.toLowerCase(),
    );
    this.cdRef.detectChanges();
    this.query.emit(query);
  }

  public getTriggerWidth(): string {
    return (
      this.listTemplate.elementRef &&
      this.listTemplate.elementRef.nativeElement &&
      this.listTemplate.elementRef.nativeElement.parentNode.offsetWidth + 'px'
    );
  }

  public trackByAttribute(index: number, attr: Attribute): string {
    return attr.value;
  }

  public updateDropdownMaxHeight(windowHeight: number): void {
    this.maxDropdownHeight = `${Math.max(windowHeight / 4, 250)}px`;
  }

  public onBackdropClick(origin: OverlayOriginDirective): void {
    if (!this.dropdownAutoCloseOnUnpreventedClick) {
      return;
    }

    this.toggleDropdown(origin);
  }

  public toggleDropdown(origin: OverlayOriginDirective): void {
    this.dropdown.origin = origin;
    this.dropdown.open = !this.dropdown.open;
    this.cdRef.detectChanges();
  }

  public toggleShowedOverLimit() {
    this.showedOverLimit = !this.showedOverLimit;
    this.setLimitedAttributes();
    this.cdRef.detectChanges();
  }

  private getRemovableAttributes(): Record<string | number, boolean> {
    if (!this.attributes || !this.attributes.length) return {};

    return this.attributes.reduce(
      (acc, attribute) => {
        acc[attribute.id!] =
          typeof this.canRemove === 'boolean' ? this.canRemove : this.canRemove(attribute);
        return acc;
      },
      <Record<string | number, boolean>>{},
    );
  }

  private setLimitedAttributes() {
    this.limitedAttributes = this.attributes.slice(
      0,
      this.showedOverLimit ? this.attributes.length : this.itemsLimit,
    );
  }
}
